"use client";

import { useId, useState } from "react";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import Button from "@/components/ui/Button/Button";
import useSolopreneurProfile from "@/hooks/useSolopreneurProfile";

/**
 * Quand aucun des trente outils ne répond, il reste quelqu'un à qui demander.
 *
 * UN OUTIL QUI REFUSE DE CONCLURE LAISSE SON LECTEUR SUR LE TROTTOIR. La
 * rubrique dit franchement ce qu'elle ne sait pas, et c'est ce qui fait sa
 * valeur ; encore faut-il que la phrase suivante ne soit pas un cul-de-sac.
 * Celui qui a une activité mixte, un cas de requalification ou une question
 * que personne n'a écrite peut écrire à l'équipe, sans compte.
 *
 * LA ROUTE EXISTE DÉJÀ, LE FORMULAIRE NON. app/api/support/contact est
 * publique, protégée par reCAPTCHA et arrive dans la boîte de l'équipe : elle
 * est réutilisée telle quelle. Le composant SupportForm, lui, ne pouvait pas
 * l'être : il lit ses libellés dans next-intl, et le layout racine ne passe
 * qu'un sous-ensemble des messages aux composants clients du groupe (public).
 * Ses douze clés seraient parties en MISSING_MESSAGE en production. Les
 * libellés sont donc portés par la page, comme tout le reste de la rubrique.
 *
 * CE QUI PART AVEC LE MESSAGE EST ÉCRIT À L'ÉCRAN. Les réponses déjà données
 * à la rubrique valent dix questions de relance pour qui répondra : elles sont
 * jointes, et le lecteur voit lesquelles avant d'envoyer.
 */

/**
 * Les libellés visibles des trois champs. Les phrases de la page restent en
 * texte indicatif : un placeholder s'efface à la première lettre, et le
 * lecteur ne savait plus ce qu'il remplissait.
 */
const LIBELLES = {
    fr: { nom: "Prénom", email: "E-mail", message: "Votre question" },
    en: { nom: "First name", email: "Email", message: "Your question" },
};

function FormulaireQuestion({ t, locale, onEnvoye, contexte }) {
    const l = LIBELLES[locale] || LIBELLES.fr;
    const id = useId();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [donnees, setDonnees] = useState({
        name: "",
        email: "",
        message: "",
    });
    const [envoi, setEnvoi] = useState(false);
    const [erreur, setErreur] = useState(null);

    const champ =
        "w-full px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-lightBlue/70 focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200";
    const etiquette = "block text-xs text-gray-600 dark:text-lightBlue/80 mb-1.5";

    const envoyer = async (e) => {
        e.preventDefault();
        setErreur(null);
        if (!executeRecaptcha) {
            setErreur(t.questionErreurRecaptcha);
            return;
        }
        setEnvoi(true);
        try {
            const recaptchaToken = await executeRecaptcha("tools_question");
            const reponse = await fetch("/api/support/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: donnees.name,
                    email: donnees.email,
                    // Le contexte suit la question : il évite un aller-retour
                    // pour redemander ce que la rubrique sait déjà.
                    message: contexte
                        ? `${donnees.message}\n\n---\n${contexte}`
                        : donnees.message,
                    recaptchaToken,
                    recaptchaVersion: "v3",
                }),
            });
            if (!reponse.ok) {
                // Un score trop bas bascule sur un second contrôle que ce
                // formulaire court ne porte pas : on envoie vers la page
                // d'aide, qui l'a, plutôt que de laisser sur un échec sec.
                setErreur(t.questionErreurEnvoi);
                setEnvoi(false);
                return;
            }
            onEnvoye();
        } catch (e) {
            setErreur(t.questionErreurEnvoi);
            setEnvoi(false);
        }
    };

    return (
        <form onSubmit={envoyer} className="space-y-3.5">
            <div>
                <label htmlFor={`${id}-nom`} className={etiquette}>
                    {l.nom}
                </label>
                <input
                    id={`${id}-nom`}
                    type="text"
                    required
                    autoComplete="given-name"
                    value={donnees.name}
                    onChange={(e) =>
                        setDonnees({ ...donnees, name: e.target.value })
                    }
                    placeholder={t.questionNom}
                    className={champ}
                />
            </div>
            <div>
                <label htmlFor={`${id}-email`} className={etiquette}>
                    {l.email}
                </label>
                <input
                    id={`${id}-email`}
                    type="email"
                    required
                    autoComplete="email"
                    value={donnees.email}
                    onChange={(e) =>
                        setDonnees({ ...donnees, email: e.target.value })
                    }
                    placeholder={t.questionEmail}
                    className={champ}
                />
            </div>
            <div>
                <label htmlFor={`${id}-message`} className={etiquette}>
                    {l.message}
                </label>
                <textarea
                    id={`${id}-message`}
                    required
                    rows={5}
                    value={donnees.message}
                    onChange={(e) =>
                        setDonnees({ ...donnees, message: e.target.value })
                    }
                    placeholder={t.questionMessage}
                    className={`${champ} resize-y`}
                />
            </div>

            {contexte && (
                <p className="text-xs text-gray-600 dark:text-lightBlue/80 mb-0">
                    {t.questionContexte.replace("{contexte}", contexte)}
                </p>
            )}

            {erreur && (
                <p role="alert" className="text-sm text-red-700 dark:text-red-400 mb-0">
                    {erreur}{" "}
                    <Link href="/support" className="underline">
                        <span className="text-red-700 dark:text-red-400">{t.questionAide}</span>
                    </Link>
                </p>
            )}

            <Button type="primary" block isLoading={envoi} progress>
                {t.questionEnvoyer}
            </Button>

            <p className="text-xs text-gray-600 dark:text-lightBlue/80 mb-0">
                {t.questionConfidentialite}
            </p>
        </form>
    );
}

export default function ToolsQuestionButton({ t, locale }) {
    const [ouverte, setOuverte] = useState(false);
    const [envoye, setEnvoye] = useState(false);
    const { profil } = useSolopreneurProfile();

    // Ce que la rubrique sait déjà, écrit en clair pour que le lecteur le
    // voie avant d'envoyer.
    const morceaux = [];
    if (profil.activite && t.activitesTitre[profil.activite]) {
        morceaux.push(t.activitesTitre[profil.activite]);
    }
    if (profil.objectifMensuel) {
        morceaux.push(
            t.questionContexteObjectif.replace(
                "{montant}",
                new Intl.NumberFormat(locale === "en" ? "en-GB" : "fr-FR", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 0,
                }).format(profil.objectifMensuel),
            ),
        );
    }
    if (profil.dateDebut) {
        morceaux.push(
            t.questionContexteDate.replace(
                "{date}",
                // La date est stockée au format ISO pour rester comparable ;
                // elle se lit en toutes lettres.
                // Intl plutôt que moment, dont la locale française n'est pas
                // chargée ici : la date sortait en anglais.
                new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                }).format(new Date(`${profil.dateDebut}T12:00:00`)),
            ),
        );
    }
    const contexte = morceaux.length ? morceaux.join(" · ") : null;

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setEnvoye(false);
                    setOuverte(true);
                }}
                className="group inline-flex items-center gap-2 rounded-full border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-4 py-2 transition-all duration-200 hover:border-primary/40 hover:-translate-y-0.5"
            >
                <SolarIcon
                    name="chat-round-line-linear"
                    className="text-base text-primary shrink-0"
                />
                {/* group-hover l'emporte sur la règle globale a:hover, qui
                    repeindrait sinon ce libellé en rose. */}
                <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                    {t.questionBouton}
                </span>
            </button>

            <BottomSheetModal
                isOpen={ouverte}
                onClose={() => setOuverte(false)}
                maxWidthClass="sm:max-w-lg"
                labelledBy={envoye ? "question-succes-titre" : "question-titre"}
            >
                <div className="p-6 sm:p-8">
                    {envoye ? (
                        <div className="text-center py-4">
                            <SolarIcon
                                name="check-circle-linear"
                                className="text-4xl text-primary"
                            />
                            <h2 id="question-succes-titre" className="text-xl font-bold text-gray-900 dark:text-white font-sourceSans mt-3 mb-1.5">
                                {t.questionSuccesTitre}
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-lightBlue mb-5">
                                {t.questionSucces}
                            </p>
                            <Button
                                type="secondary"
                                onClick={() => setOuverte(false)}
                            >
                                {t.questionFermer}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h2 id="question-titre" className="text-xl font-bold text-gray-900 dark:text-white font-sourceSans mb-1.5">
                                {t.questionTitre}
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-lightBlue mb-5">
                                {t.questionSousTitre}
                            </p>
                            <GoogleReCaptchaProvider
                                reCaptchaKey={
                                    process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY
                                }
                                scriptProps={{ async: true, defer: true }}
                            >
                                <FormulaireQuestion
                                    t={t}
                                    locale={locale}
                                    contexte={contexte}
                                    onEnvoye={() => setEnvoye(true)}
                                />
                            </GoogleReCaptchaProvider>
                        </>
                    )}
                </div>
            </BottomSheetModal>
        </>
    );
}
